<template>
  <div>

    <div class="row mb-3">
      <div class="col-9">
        <h2 class="h3 mb-2">{{ __('label:fee-summary title') }}</h2>
        <p>See a summary of your fees below. This summary is based on the units you have selected and excludes any units you have indicated are already
          completed.</p>
      </div>
      <div class="col-3 text-right print-hide">
        <button class="btn-icon-round btn-solid-dark btn-lg btn-hover-expand" v-on:click="printScreen"><i class="far fa-print"></i><span
            class="text">Print / Save as PDF</span></button>
      </div>
    </div>

    <!-- Fee Summary content... -->
    <div class="row" v-if="loading">
      <div class="page-content-state page-content-state-loading col-12">
        <span class="page-content-state__icon  far fa-spinner-third fa-spin"></span>
        <div class="page-content-state__content">Loading Fee Summary</div>
      </div>
    </div>
    <div class="row" v-else-if="error">
      <div class="page-content-state col-12">
        <span class="page-content-state__icon fal fa-sad-cry"></span>
        <div class="page-content-state__header">Sorry, there was a problem loading your fee summary.</div>
        <button class="btn btn-primary mt-3" id="tooltip-regenerate-btn" v-on:click="loadFees">Try again</button>
      </div>
    </div>
    <div class="row" v-else>
      <div class="col-12 col-xl-8">

        <div class="fee-sum-head">
          <div class="row">
            <div class="col-8">
              <dl class="value-pair-lg">
                <dt>{{ course.code }}</dt>
                <dd>{{ course.name }}</dd>
              </dl>
            </div>
            <div class="col-4 text-right">
              <dl class="value-pair-lg mb-1">
                <dt>Estimated cost</dt>
                <dd>{{ totalSelectedUnitFees }}</dd>
              </dl>
              <span v-if="feesLoaded" class="font-sm">
                For {{selectedCredits}} / {{course.requiredCredits}} credit points<br />(excludes completed and duplicate units)
              </span>
            </div>

            <div class="page-content-state page-content-state-loading col-12 pt-2 pb-1" v-if="!feesLoaded">
              <span class="page-content-state__icon  far fa-spinner-third fa-spin"></span>
              <div class="page-content-state__content">Loading fee details</div>
            </div>

          </div>
        </div>

        <!-- Fee breakdown -->
        <div class="mt-4" v-if="course">
          <FeeStructure :structure="course.structure[0]" :location="location" :mandatory="true" />
        </div>

        <!-- Alert / caveat block -->
        <div class="page-alert warning fee-disclaimer mt-5 mb-0">
          <div class="page-alert__icon">
            <i class="far fa-exclamation-circle"></i>
          </div>
          <div>
            <div class="page-alert__title">
              The fee summary provides an estimated, indicative fee.
            </div>
            <div class="page-alert__body">
              <p>The fee summary provides an estimated, indicative fee. It should only be used as a guide.
                Not all enrolment, course and fee type combinations represent available options. Students should refer to their Letter of Offer for specific
                course and fee related information.
                The fee summary assumes completion of the course within a standard course duration at a full time study load, commencing in the first
                teaching period for the commencement year you have selected.</p>
              <p>The estimated, indicative fee assumes no fee increases or indexation. Such fee increases or indexation may occur and will affect the
                estimate. In the case of a full fee paying student, this may occur at the start of your second and each subsequent year of study. In the case
                of Commonwealth supported places, this may occur for each new calendar year.</p>
              <p>The estimated, indicative fee: (a) is summary based on the units selected and their associated courses, and may change if different units
                or courses are selected; (b) is for tuition fees only, and certain additional fees and charges will apply (c) is in Australian dollars; and
                (d) does not account for any scholarships for which you may be eligible.</p>
            </div>
          </div>
        </div>

        <div class="mt-5"><strong>This Fee Summary was generated on {{ getDateTime }} AWST.</strong></div>

        <div class="mt-5 print-only">
          <h6>Disclaimer</h6>
          <ul>
            <li>The Course Visualiser assists with the generation of a Course Plan. It should not be enacted until it is validated with the Academic Chair of
              the Course.</li>
            <li>This Course Plan is based on the course structure and offerings of latest Approved Courses only. It is the responsibility of students to
              ensure the correct availability of units in each semester/trimester of each academic year.</li>
            <li>Please contact the Academic Chair of the Course if you have any queries relating to the Course Plan.</li>
          </ul>
        </div>

      </div>

    </div>

  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import Formatter from '../../common/helpers/formatter'
import moment from 'moment'

import Banner from './common/Banner.vue'
import FeeStructure from './fee-summary/FeeStructure.vue'
import FeeUnitBlock from './fee-summary/FeeUnitBlock.vue'

export default {
  name: 'CourseFeeSummaryView',
  // TODO: remove comment once the select-unit page is stable
  setup(props, context) {
    return {
      // baseStep: useBaseStep(props, context),
      loading: false,
      error: null,
      credits: 0
    }
  },

  data() {
    return {
      location: 'root'
    }
  },

  computed: {
    ...mapGetters({
      course: 'courses/getSelectedCourse',

      selectedPreferences: 'preferences/getSelectedStudyPreferences',
      feesPreferences: 'preferences/getFeesPreferences',
      feesLoaded: 'preferences/getFeesLoaded',

      selectedItems: 'units/getSelectedCurriculumItems',
      completedUnits: 'units/getCompletedUnits',
      totalCredits: 'units/getTotalCredits'
    }),

    selectedCredits() {
      return this.totalCredits('root', false, false) || 0
    },
    selectedUnits() {
      return this.selectedItems.filter((i) => i.relationship.type === 'Unit')
    },
    selectedUnitArray() {
      return this.selectedUnits.filter((u) => !this.completed(u.relationship.code)).map((u) => u.relationship.unit)
    },
    totalSelectedUnitFees() {
      return this.feesLoaded ? Formatter.FormatCurrency(this.feesPreferences.calculator.calculateTotal(this.selectedUnitArray)) : null
    },
    getDateTime: function () {
      return moment().utcOffset(8).format('DD/MM/YY, h:mm a')
    }
  },
  methods: {
    ...mapActions({
      fetchSelectedCourseFees: 'preferences/fetchSelectedCourseFees'
    }),
    printScreen() {
      window.print()
    },
    completed(code) {
      return this.completedUnits.includes(code)
    },
    async loadFees() {
      this.loading = true
      this.error = null
      if (this.selectedPreferences.studentType !== 'Transnational') {
        try {
          await this.fetchSelectedCourseFees()
        } catch (err) {
          this.error = err
        }
      }
      this.loading = false
    }
  },
  mounted: async function () {
    await this.loadFees()
  },
  components: { Banner, FeeStructure, FeeUnitBlock }
}
</script>
